import { BOOKING_CAMPAIGN_URL_PARAM } from './translation_namespace/booking_source_namespace';

/**
 * Référence de campagne côté navigateur (attribution des réservations prises sur le site du pro
 * ou via le widget après un clic sur un lien de campagne `?cid=<pro_campaign_id>`).
 *
 * Deux mémoires :
 * - **mémoire de visite** (variable de module) : survit aux navigations client du site, jamais au
 *   rechargement — aucun stockage terminal, donc aucun consentement requis ;
 * - **mémoire persistée** (`localStorage`, 7 jours, clé technique de première partie sans donnée
 *   personnelle) : posée seulement si l'appelant l'autorise (`persist`), c'est-à-dire avec le
 *   consentement marketing du visiteur — même règle que le cookie d'affiliation.
 *
 * Le backend ne retient le `campaign_id` que s'il désigne une campagne envoyée du pro ; ici on
 * ne fait que transporter l'identifiant.
 */
export const CAMPAIGN_REF_STORAGE_KEY = 'propelsia_campaign_ref';
export const CAMPAIGN_REF_TTL_DAYS = 7;
const CAMPAIGN_REF_TTL_MS = CAMPAIGN_REF_TTL_DAYS * 24 * 60 * 60 * 1000;

export interface CampaignRef {
  /** Slug du pro dont le lien de campagne portait la référence. */
  slug: string;
  /** `pro_campaign_id` (entier positif). */
  cid: number;
  /** ISO : au-delà, la référence est périmée. */
  exp: string;
}

let visitRef: CampaignRef | null = null;

/** Valeur de `?cid=` → entier positif, `null` sinon (jamais d'erreur côté client). */
export function parseCampaignIdParam(value: string | string[] | null | undefined): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string' || !/^\d{1,12}$/.test(raw.trim())) return null;
  const parsed = Number.parseInt(raw.trim(), 10);
  return parsed > 0 ? parsed : null;
}

/** Lit `?cid=` dans une query string (`window.location.search`), `null` si absent ou invalide. */
export function readCampaignIdFromSearch(search: string): number | null {
  try {
    return parseCampaignIdParam(new URLSearchParams(search).get(BOOKING_CAMPAIGN_URL_PARAM));
  } catch {
    return null;
  }
}

function readPersistedRef(now: number): CampaignRef | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CAMPAIGN_REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CampaignRef>;
    if (typeof parsed.slug !== 'string' || typeof parsed.exp !== 'string') return null;
    if (!Number.isInteger(parsed.cid) || (parsed.cid as number) <= 0) return null;
    if (new Date(parsed.exp).getTime() <= now) return null;
    return { slug: parsed.slug, cid: parsed.cid as number, exp: parsed.exp };
  } catch {
    return null;
  }
}

/**
 * Retient la référence pour la visite ; la persiste 7 jours seulement si `persist` est vrai
 * (consentement marketing). Dernier clic gagnant : une nouvelle campagne remplace l'ancienne.
 */
export function rememberCampaignRef(
  slug: string,
  cid: number,
  options: { persist: boolean },
  now: number = Date.now(),
): void {
  if (!slug || !Number.isInteger(cid) || cid <= 0) return;
  const ref: CampaignRef = { slug, cid, exp: new Date(now + CAMPAIGN_REF_TTL_MS).toISOString() };
  visitRef = ref;
  if (!options.persist || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CAMPAIGN_REF_STORAGE_KEY, JSON.stringify(ref));
  } catch {
    /* stockage indisponible (navigation privée…) : la mémoire de visite suffit. */
  }
}

/** Référence encore valide pour CE pro (mémoire de visite d'abord, puis persistée), sinon `null`. */
export function readCampaignRef(slug: string, now: number = Date.now()): number | null {
  if (!slug) return null;
  if (visitRef && visitRef.slug === slug && new Date(visitRef.exp).getTime() > now) return visitRef.cid;
  const persisted = readPersistedRef(now);
  return persisted && persisted.slug === slug ? persisted.cid : null;
}

/**
 * Oublie la seule référence PERSISTÉE (retrait du consentement marketing) : la mémoire de visite,
 * sans stockage terminal, reste — elle ne demande aucun consentement.
 */
export function forgetPersistedCampaignRef(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(CAMPAIGN_REF_STORAGE_KEY);
  } catch {
    /* rien à faire */
  }
}

/** Oublie la référence (mémoire de visite et persistée). */
export function forgetCampaignRef(): void {
  visitRef = null;
  forgetPersistedCampaignRef();
}
